import React, { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { FeeValues } from '../types/flow';
import NavRow from '../../assessment-portal/components/navigation-row';
import BottomSheet from '../../assessment-portal/components/bottom-sheet';
import AsdPathwayCard from '../components/asd-pathway-card';
import FeeSummaryDetails from '../components/fee-summary-details';
import FeeUnavailableAlert from '../components/fee-unavailable-alert';
import FeeAmountLoading from '../components/fee-amount-loading';
import {
  FEE_ASSESSMENT_TYPE,
  hasMedicareOrDvaCard,
  isAutismAssessment,
} from '../utils/fee';
import useFeeQuote from '../hooks/use-fee-quote';

interface FeeEstimatePageProps {
  defaultValues: FeeValues;
  patientState: string;
  medicareType: string;
  onBack: () => void;
  onNext: (_values: FeeValues) => void;
}

interface QuestionOption {
  id: string;
  emoji: string;
  label: string;
  sub?: string;
  note?: React.ReactNode;
}

/**
 * Mobile-only control shape per question. Desktop always renders the emoji card grid.
 * - stack: tappable cards with a radio dot (assessment type, prescribing)
 * - segmented: pill switch (location)
 * - yesno: two full-width buttons (seen psychiatrist)
 */
type MobileControl = 'stack' | 'segmented' | 'yesno';

interface Question {
  id: keyof FeeValues;
  question: string;
  hint: React.ReactNode;
  /** The hint quotes a fee that does not apply to patients without a card. */
  hideHintWithoutCard?: boolean;
  /** Replaces `hint` for patients with neither a Medicare nor a DVA card. */
  noCardHint?: React.ReactNode;
  mobileControl: MobileControl;
  hideWhen?: (_values: FeeValues) => boolean;
  options: QuestionOption[];
}

const getSelectedOption = (q: Question, values: FeeValues) => q.options.find((o) => o.id === values[q.id]);

const PRESCRIPTION_NOTE = (
  <>
    If clinically appropriate, our psychiatrist may provide your initial Schedule 8 (S8)
    prescription. Ongoing prescriptions are then managed by your GP. This service covers the
    initial prescription only. ADHD diagnosis and medication are subject to the psychiatrist&apos;s
    clinical assessment.
  </>
);

const HEALTH_WORKFORCE_LOCATOR_LINK = (
  <a
    href="https://www.health.gov.au/resources/apps-and-tools/health-workforce-locator"
    target="_blank"
    rel="noopener noreferrer"
    className="text-brand underline hover:text-brand-dark"
  >
    Health Workforce Locator
  </a>
);

const QUESTIONS: Question[] = [
  {
    id: 'feeAssessmentScope',
    question: 'What are you seeking an assessment for?',
    hint: 'Choose ADHD, Autism, or both if you’d like to be assessed for each.',
    mobileControl: 'stack',
    options: [
      {
        id: 'adhd', emoji: '🧠', label: 'ADHD', sub: 'ADHD assessment only',
      },
      {
        id: 'autism', emoji: '🧩', label: 'Autism', sub: 'Autism (ASD) assessment only',
      },
      {
        id: 'combined', emoji: '🔗', label: 'ADHD + Autism', sub: 'Assessed for both conditions',
      },
    ],
  },
  {
    id: 'prescriptionType',
    question: 'Who will start your ADHD prescription if you’re diagnosed?',
    hint: 'Patients in WA, or whose GPs can’t initiate scripts, typically choose the Script Start Package.',
    mobileControl: 'stack',
    hideWhen: (values) => isAutismAssessment(values.feeAssessmentScope),
    options: [
      {
        id: 'gp', emoji: '🩺', label: 'My GP', sub: 'Standard · your GP initiates & manages your scripts',
      },
      {
        id: 'telecare',
        emoji: '🔗',
        label: 'Telecare psychiatrist',
        sub: 'Script Start Package · for WA or GPs who can’t initiate S8',
        note: PRESCRIPTION_NOTE,
      },
    ],
  },
  {
    id: 'location',
    question: 'Where are you located?',
    hint: (
      <>
        Rural and regional clients may have different service options. Not sure? Check your MM classification using the
        {' '}
        {HEALTH_WORKFORCE_LOCATOR_LINK}
        .
      </>
    ),
    // Patients without a card are priced on Metro wherever they live.
    noCardHint: (
      <>
        Without a Medicare or DVA card, the fee is the same for Metro and Rural / Regional. Not sure? Check your MM classification using the
        {' '}
        {HEALTH_WORKFORCE_LOCATOR_LINK}
        .
      </>
    ),
    mobileControl: 'segmented',
    options: [
      {
        id: 'metro', emoji: '🏙️', label: 'Metro', sub: 'Major city area',
      },
      {
        id: 'rural', emoji: '🌾', label: 'Rural / Regional', sub: 'Outside major city',
      },
    ],
  },
  {
    id: 'seenPsychiatrist',
    question: 'Have you seen another psychiatrist in the last 12 months?',
    hint: 'A recent psychiatric history may require additional review (+$300).',
    hideHintWithoutCard: true,
    mobileControl: 'yesno',
    options: [
      {
        id: 'no', emoji: '✖️', label: 'No', sub: 'No recent psychiatric review',
      },
      {
        id: 'yes', emoji: '✔️', label: 'Yes', sub: 'Seen within the last 12 months',
      },
    ],
  },
];

const FeeEstimatePage: FC<FeeEstimatePageProps> = ({
  defaultValues,
  patientState,
  medicareType,
  onBack,
  onNext,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);

  const methods = useForm<FeeValues>({
    defaultValues,
  });

  const { watch, setValue, handleSubmit } = methods;
  const values = watch();

  const visibleQuestions = useMemo(
    () => QUESTIONS.filter((q) => !(q.hideWhen && q.hideWhen(values))),
    [values],
  );

  const [activeId, setActiveId] = useState<keyof FeeValues>(() => {
    const firstUnanswered = QUESTIONS.find((q) => !defaultValues[q.id]);
    return firstUnanswered ? firstUnanswered.id : QUESTIONS[0].id;
  });

  const resolvedActiveId = visibleQuestions.some((q) => q.id === activeId)
    ? activeId
    : (visibleQuestions.find((q) => !values[q.id]) || visibleQuestions[visibleQuestions.length - 1]).id;

  const handleSelect = (qId: keyof FeeValues, optId: string) => {
    setValue(qId, optId as any);

    const nextValues = { ...values, [qId]: optId };
    const nextVisible = QUESTIONS.filter((q) => !(q.hideWhen && q.hideWhen(nextValues)));
    if (qId === 'feeAssessmentScope' && optId === FEE_ASSESSMENT_TYPE.autism && values.prescriptionType) {
      setValue('prescriptionType', '' as any);
    }

    const currentQ = QUESTIONS.find((q) => q.id === qId);
    const revealsNote = Boolean(
      currentQ?.options.find((o) => o.id === optId)?.note,
    );
    if (revealsNote) {
      setActiveId(qId);
      return;
    }

    const idx = nextVisible.findIndex((q) => q.id === qId);
    const next = nextVisible.slice(idx + 1).find((q) => !nextValues[q.id]);
    setActiveId(next ? next.id : qId);
  };

  const totalVisible = visibleQuestions.length;
  const totalAnswered = visibleQuestions.filter((q) => values[q.id]).length;
  const allDone = totalAnswered === totalVisible;

  const feeQuote = useFeeQuote(patientState, medicareType, values);
  const hasMedicareCard = hasMedicareOrDvaCard(medicareType);
  const hintFor = (q: Question): React.ReactNode => {
    if (hasMedicareCard) return q.hint;
    if (q.hideHintWithoutCard) return null;
    return q.noCardHint ?? q.hint;
  };
  const localFee = allDone ? feeQuote.quote : null;
  const feeUnavailable = allDone && feeQuote.status === 'error';
  const feeLoading = allDone && feeQuote.status === 'loading';

  const feeRowStatus = (): string => {
    if (localFee) return hasMedicareCard ? 'Private fee · no Medicare rebate' : 'Private fee';
    if (!allDone) return `${totalAnswered} of ${totalVisible} answered`;
    return feeUnavailable ? 'Fee unavailable' : 'Loading fee…';
  };

  const handleNext = handleSubmit((data) => onNext(data));

  const getContainerClass = (isActive: boolean, answered: boolean) => {
    if (isActive) return 'border-brand bg-white shadow-md p-4 ring-1 ring-brand ring-opacity-10';
    if (answered) return 'border-brand/40 bg-blue-50/30 p-3.5 cursor-pointer hover:bg-blue-50/50';
    return 'border-slate-200 bg-slate-50 p-3.5 cursor-default';
  };

  const money = (amount: number) => `$${amount.toLocaleString('en-AU')}.00`;

  const selectOption = (e: React.MouseEvent, qId: keyof FeeValues, optId: string) => {
    e.stopPropagation();
    handleSelect(qId, optId);
  };

  const renderStackOptions = (q: Question) => (
    <div className="flex flex-col gap-2" role="group" aria-label={q.question}>
      {q.options.map((opt) => {
        const selected = values[q.id] === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={selected}
            onClick={(e) => selectOption(e, q.id, opt.id)}
            className={`min-h-option-card flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
              selected ? 'border-brand bg-blue-50/50' : 'border-slate-200 bg-white'
            }`}
          >
            <span className="text-xl flex-shrink-0" aria-hidden="true">{opt.emoji}</span>
            <span className="flex-1 min-w-0">
              <span className={`block text-sm font-bold leading-tight ${selected ? 'text-brand' : 'text-slate-800'}`}>
                {opt.label}
              </span>
              {opt.sub && (
                <span className="block text-xs text-slate-500 leading-snug mt-0.5">{opt.sub}</span>
              )}
            </span>
            <span
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                selected ? 'border-brand' : 'border-slate-300'
              }`}
            >
              {selected && <span className="w-2.5 h-2.5 rounded-full bg-brand block" />}
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderSegmentedOptions = (q: Question) => (
    <div className="flex gap-2" role="radiogroup" aria-label={q.question}>
      {q.options.map((opt) => {
        const selected = values[q.id] === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={(e) => selectOption(e, q.id, opt.id)}
            className={`flex-1 min-h-yn-btn px-3 rounded-xl border-2 text-sm leading-tight transition-all ${
              selected
                ? 'bg-brand border-brand text-white font-semibold'
                : 'bg-white border-slate-200 text-slate-700 font-medium'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  const renderYesNoOptions = (q: Question) => (
    <div className="flex gap-2" role="radiogroup" aria-label={q.question}>
      {q.options.map((opt) => {
        const selected = values[q.id] === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={(e) => selectOption(e, q.id, opt.id)}
            className={`flex-1 min-h-yn-btn rounded-md border text-sm transition-all ${
              selected
                ? 'bg-brand border-brand text-white font-semibold'
                : 'bg-white border-slate-200 text-slate-700 font-medium'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  const renderMobileControl = (q: Question) => {
    if (q.mobileControl === 'segmented') return renderSegmentedOptions(q);
    if (q.mobileControl === 'yesno') return renderYesNoOptions(q);
    return renderStackOptions(q);
  };

  const feeFooterRow = (
    <button
      type="button"
      className="adhd-fee-row"
      onClick={() => localFee && setSheetOpen(true)}
      disabled={!localFee}
    >
      <span>
        <b>Estimated fee</b>
        <small>{feeRowStatus()}</small>
      </span>
      <span>
        <span className="adhd-fee-row__amount">
          {feeLoading ? <FeeAmountLoading /> : (localFee && money(localFee.gross)) || '$—'}
        </span>
        {localFee && <em className="adhd-fee-row__details">Details ⌃</em>}
      </span>
    </button>
  );

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-none md:rounded-2xl shadow-sm border border-slate-200 p-4 md:p-8">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-1 text-brand-dark">Fee Estimate</h2>
        <p className="text-slate-500 text-sm mb-4">Answer a few quick questions — we&apos;ll calculate your intake fee instantly.</p>

        <div className="mb-6 hidden md:block">
          <div className="flex justify-between mb-1.5">
            <span className="text-xs text-slate-500">
              {totalAnswered}
              {' '}
              of
              {' '}
              {totalVisible}
              {' '}
              answered
            </span>
            {allDone && <span className="text-xs text-brand font-semibold">✓ All done!</span>}
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand rounded-full transition-all duration-500 ease-out"
              style={{ width: `${(totalAnswered / totalVisible) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {visibleQuestions.map((q, qi) => {
            const answered = values[q.id];
            const isActive = resolvedActiveId === q.id;
            const currentAnswer = q.options.find((o) => o.id === answered);
            const selectedNote = getSelectedOption(q, values)?.note;

            return (
              <div
                key={q.id}
                role="button"
                tabIndex={0}
                onClick={() => setActiveId(q.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setActiveId(q.id);
                  }
                }}
                className={`
                  rounded-xl border transition-all duration-300 ease-in-out overflow-hidden
                  ${getContainerClass(isActive, !!answered)}
                `}
              >
                <div className={`flex items-center gap-3 ${isActive ? 'mb-4' : ''}`}>
                  <div
                    className={`
                      w-6 h-6 rounded-full flex items-center justify-center text-step-label font-bold text-white transition-colors duration-300 flex-shrink-0
                      ${answered || isActive ? 'bg-brand' : 'bg-slate-300'}
                    `}
                  >
                    {answered ? '✓' : qi + 1}
                  </div>
                  <div className="flex-1">
                    <span className="font-semibold text-sm text-slate-800">{q.question}</span>
                    {answered && !isActive && (
                      <span className="ml-2 text-xs font-medium text-brand">
                        ·
                        {' '}
                        {currentAnswer?.label}
                      </span>
                    )}
                  </div>
                  {answered && !isActive && (
                    <span className="text-step-label text-slate-400 whitespace-nowrap">tap to change</span>
                  )}
                </div>

                {isActive && (
                  <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                    {hintFor(q) && (
                      <p className="text-xs text-slate-500 mb-3.5 leading-relaxed">
                        💡
                        {' '}
                        {hintFor(q)}
                      </p>
                    )}
                    <div className="md:hidden">
                      {renderMobileControl(q)}
                    </div>

                    <div className="hidden md:flex md:flex-wrap gap-2.5">
                      {q.options.map((opt) => {
                        const selected = values[q.id] === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={(e) => selectOption(e, q.id, opt.id)}
                            className={`
                              flex-1 min-w-btn p-3 rounded-lg border-2 text-left transition-all cursor-pointer outline-none flex items-center gap-3
                              ${selected
                              ? 'border-brand bg-blue-50/50'
                              : 'border-slate-200 bg-white hover:border-slate-300'
                              }
                            `}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-xl mb-1.5">{opt.emoji}</div>
                              <div className={`text-sm font-semibold mb-0.5 ${selected ? 'text-brand' : 'text-slate-800'}`}>
                                {opt.label}
                              </div>
                              <div className={`text-xs ${selected ? 'text-brand/80' : 'text-slate-400'}`}>
                                {opt.sub}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {selectedNote && (
                      <div className="mt-3 px-3.5 py-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-2 items-start">
                        <span className="text-base flex-shrink-0" aria-hidden="true">ℹ️</span>
                        <p className="m-0 text-xs text-blue-800 leading-relaxed">{selectedNote}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 pt-5 border-t border-slate-100 hidden md:block">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Your estimated</p>
          <div className="flex justify-between items-center">
            <p className="text-base font-semibold text-slate-800">Consultation fee</p>
            <div className={`text-3xl font-bold transition-colors duration-300 ${allDone && localFee ? 'text-brand' : 'text-slate-300'}`}>
              {feeLoading ? <FeeAmountLoading size="lg" /> : (localFee && money(localFee.gross)) || '$—'}
            </div>
          </div>

          {localFee?.includesPsychiatricReviewFee && (
            <p className="text-xs text-slate-500 mt-2">
              <span className="font-semibold text-green-600">+ $300</span>
              {' '}
              recent psychiatric history review included.
            </p>
          )}

          {allDone && localFee && localFee.hasAsdPathway && (
            <AsdPathwayCard consultationFee={localFee.gross} hasMedicareCard={hasMedicareCard} />
          )}

          <p className="mt-3 text-xs text-muted leading-relaxed">
            This fee has been calculated based on the information you provided. If your appointment
            is within the next 7 days, please pay now to secure your booking. Our team will verify
            your eligibility and details afterwards and contact you if any fee adjustment is required.
          </p>

          <p className="mt-3 text-xs text-black leading-relaxed font-semibold">
            {hasMedicareCard
              ? 'Please note: This is a private fee, and NO Medicare rebate applies to this fee.'
              : 'Please note: This is a private fee.'}
            {allDone && localFee && localFee.hasAsdPathway && (
              <>
                {' '}
                If you are eligible, your first psychiatrist appointment following the intake
                appointment
                {hasMedicareCard ? ' will be bulk billed.' : ' will have no upfront fee.'}
              </>
            )}
          </p>
        </div>
      </div>

      {feeUnavailable && <FeeUnavailableAlert onRetry={feeQuote.reload} />}

      <NavRow
        onBack={onBack}
        onNext={handleNext}
        nextDisabled={!allDone || !localFee}
        nextLabel="Continue to Medical History →"
        footerSlot={feeFooterRow}
        hint={allDone ? undefined : 'Answer all questions to see your estimate'}
      />

      {localFee && (
        <BottomSheet
          visible={sheetOpen}
          title="Fee summary"
          onClose={() => setSheetOpen(false)}
        >
          <FeeSummaryDetails
            feeEstimate={localFee}
            feePrescriptionType={values.prescriptionType}
            feeLocation={values.location}
            feeAssessmentType={values.feeAssessmentScope}
            feeSeenPsychiatrist={values.seenPsychiatrist}
            hasMedicareCard={hasMedicareCard}
            compact
          />
        </BottomSheet>
      )}
    </div>
  );
};

export default FeeEstimatePage;
